import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { Box, Button, CircularProgress, Stack } from "@mui/joy";
import { theme } from "styles/theme";
import DefaultPage from "pages/container/DefaultPage";
import CustomTypography from "components/common/CustomTypography";
import useAutoLoginPromotion from "hooks/useAutoLoginPromotion";
import useSafeAreaColor from "hooks/useSafeAreaColor";
import useMixpanelMKT from "hooks/useMixpanelMKT";
import { userSimpleAtom } from "recoil/userSimpleAtom";
import { referralApi } from "api/referralApi";
import useSaveReferralCode from "hooks/useSaveReferralCode";
import { captureException } from "utils/sentry";
import { PATH } from "../../routes/paths";
import distancingLogo from "../../static/image/common/distancing_logo.png";
import { isDev } from "config";
import { buildResumeInstallDeepLink } from "data/deepLinkData";
import useAccountIdHash from "hooks/useAccountIdHash";

// 레퍼럴 링크 진입 화면 — distancing.im/with/:code
// 인트로(간지)를 먼저 보여준 뒤 CTA 에서 로그인/온보딩으로 분기한다.
// 코드는 저장소가 아니라 URL 로 로그인 화면에 넘긴다 (거기서 OAuth state 로 왕복).
export default function ReferralEntryPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const user = useRecoilValue(userSimpleAtom);
  const accountIdHash = useAccountIdHash(user?.userId);
  const saveReferralCode = useSaveReferralCode();

  // refresh-token 쿠키로 자동 로그인 시도 (백그라운드, 인트로 표시는 막지 않음)
  useAutoLoginPromotion();

  const backgroundColor = theme.vars.palette.Background.Level3;
  useSafeAreaColor(backgroundColor);

  // 게이트 유효성 검사: checking=검사중, valid=유효(인트로 노출), expired=기간 만료(만료 안내 노출).
  // 무효/에러는 즉시 우회 navigate.
  const [gate, setGate] = useState<"checking" | "valid" | "expired">(
    "checking"
  );
  // 화면 노출용 제휴사 이름(코드 아님). 없으면 코드로 폴백.
  const [referralName, setReferralName] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    if (!code) {
      navigate("/promotion-onboarding/start/1", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await referralApi.validate(code);
        if (cancelled) return;
        if (res.code === 0 && res.result?.isValid) {
          setReferralName(res.result.displayName || res.result.name || code);
          setGate("valid");
        } else if (res.code === 0 && res.result?.isExpired) {
          // 기간 만료 → 만료 안내 화면 (코드 미보관)
          setGate("expired");
        } else {
          // 무효 → 일반 promotion 온보딩으로 우회 (코드 미보관)
          navigate("/promotion-onboarding/start/1", { replace: true });
        }
      } catch (e) {
        if (cancelled) return;
        captureException(e, {
          tags: { action: "affiliate_referral_validate" },
        });
        navigate("/promotion-onboarding/start/1", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // 기로그인 상태(자동 로그인 포함)로 진입한 경우, CTA 클릭을 기다리지 않고 진입 즉시 코드를 영속화한다
  // — 정산 기여 윈도우(+3일)가 "진입 시점" 기준으로도 연장되도록. 실패 시 CTA 폴백에서 재시도.
  const hasSavedOnEntry = useRef(false);
  useEffect(() => {
    if (
      gate !== "valid" ||
      !code ||
      !user?.accessToken ||
      hasSavedOnEntry.current
    )
      return;
    hasSavedOnEntry.current = true;
    saveReferralCode(code).then((saved) => {
      // 실패했으면 플래그를 되돌려 CTA 폴백에서 재시도한다.
      if (!saved) hasSavedOnEntry.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate, code, user?.accessToken]);

  // 진입 트래킹 (promotion 의 "로그인 페이지 진입" 과 동등). promotion named 인스턴스로 1회 발화.
  const trackReferralEntry = useMixpanelMKT({
    eventName: "제휴 페이지 진입",
    params: { code: code ?? "" },
    version: "v4",
  });
  const hasTrackedEntry = useRef(false);
  useEffect(() => {
    if (hasTrackedEntry.current) return;
    hasTrackedEntry.current = true;
    trackReferralEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = async () => {
    if (user?.accessToken) {
      // 이미 로그인 상태 → 코드 영속화 후 결과 화면으로 바로 진입 (로그인 생략).
      // 진입 시점에 이미 저장됐으면(hasSavedOnEntry) 중복 호출 생략.
      if (code && !hasSavedOnEntry.current) {
        await saveReferralCode(code);
      }
      navigate(PATH.referralResult, { replace: true });
    } else {
      // 미로그인 → 제휴 전용 로그인 페이지로. 코드는 URL 로 넘긴다
      // (거기서 다시 OAuth state 로 실려 리다이렉트를 왕복한다).
      navigate(`${PATH.referralLogin}?code=${encodeURIComponent(code ?? "")}`);
    }
  };

  if (gate === "checking") {
    return (
      <Box
        sx={{
          width: "100%",
          height: "calc(var(--vh,1vh) * 100)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: backgroundColor,
        }}
      >
        <CircularProgress color="neutral" />
      </Box>
    );
  }

  if (gate === "expired") {
    // 기간이 끝난 프로모션 안내(affiliate/discount 공통). 확인 → 앱 실행/설치 OneLink.
    return (
      <DefaultPage
        backgroundColor={backgroundColor}
        content={
          <Stack alignItems="center" spacing={5}>
            <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
              <img
                src={distancingLogo}
                alt="distancing logo"
                style={{ width: "138px", height: "auto" }}
              />
              <CustomTypography
                variant="Subtitle"
                sx={{
                  textAlign: "center",
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 500,
                  whiteSpace: "pre-wrap",
                }}
              >
                {"구독자 전용 우대 혜택\n프로모션이 종료되었습니다."}
              </CustomTypography>
            </Stack>
            <Button
              onClick={() =>
                window.open(
                  buildResumeInstallDeepLink(
                    isDev,
                    user?.authenticateType,
                    accountIdHash
                  ),
                  "_blank"
                )
              }
              variant="Solid"
              color="primary"
              size="lg"
              sx={{
                width: "100%",
                maxWidth: "345px",
                height: "54px",
                fontWeight: 700,
                borderRadius: "12px",
                fontSize: "16px",
              }}
            >
              {"확인"}
            </Button>
          </Stack>
        }
      />
    );
  }

  return (
    <DefaultPage
      backgroundColor={backgroundColor}
      content={
        <Stack alignItems="center" spacing={5}>
          <Stack spacing={4} alignItems="center" sx={{ width: "100%" }}>
            <img
              src={distancingLogo}
              alt="distancing logo"
              style={{ width: "138px", height: "auto" }}
            />
            <Stack spacing={"12px"} alignItems="center" sx={{ width: "100%" }}>
              <CustomTypography
                variant="H2"
                sx={{
                  textAlign: "center",
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 700,
                  whiteSpace: "pre-wrap",
                }}
              >
                <Box
                  component="span"
                  sx={{ color: theme.vars.palette.Content.Highlight }}
                >
                  {referralName}
                </Box>
                {"님이 당신의\n마음 건강을 응원합니다."}
              </CustomTypography>
              <CustomTypography
                variant="Subtitle"
                sx={{
                  textAlign: "center",
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 500,
                  whiteSpace: "pre-wrap",
                }}
              >
                {"아래 버튼을 통해 시작하시면\n"}
                <Box
                  component="span"
                  sx={{
                    color: theme.vars.palette.Content.Highlight,
                    fontWeight: 700,
                  }}
                >
                  {"3만 원"}
                </Box>
                {"의 특별 우대 혜택이 적용됩니다."}
              </CustomTypography>
            </Stack>
          </Stack>
          <Stack spacing={"12px"} alignItems="center" sx={{ width: "100%" }}>
            <Button
              onClick={handleStart}
              variant="Solid"
              color="primary"
              size="lg"
              sx={{
                width: "100%",
                maxWidth: "345px",
                height: "54px",
                fontWeight: 700,
                borderRadius: "12px",
                fontSize: "16px",
              }}
            >
              {"할인 받고 시작하기"}
            </Button>
            <CustomTypography
              variant="Caption"
              sx={{
                textAlign: "center",
                color: theme.vars.palette.Content.Weak,
                fontWeight: 500,
              }}
            >
              {"본 프로모션은 예고 없이 종료될 수 있습니다."}
            </CustomTypography>
          </Stack>
        </Stack>
      }
    />
  );
}
